import React, { useEffect, useState } from "react";
import {
  FaSearch,
  FaFileExcel,
  FaFileCsv,
  FaFilePdf,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getData } from "../../../services/FetchNodeservices";

export default function BusinessReport() {
  const [filters, setFilters] = useState({
    clientName: "",
    clientStatus: "",
    vertical: "",
    callingStatus: "",
  });

  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const columns = [
    "date",
    "clientName",
    "clientStatus",
    "vertical",
    "spocName",
    "spocPhone",
    "spocEmail",
    "callingStatus",
    "callingFrequency",
    "businessProposal",
    "remarks",
    "currentCompany",
    "previousCompany",
  ];

  useEffect(() => {
    loadData();
  }, []);

  const safe = (v) =>
    v === null || v === undefined || v === "null" ? "" : v;

  const loadData = async () => {
    const res = await getData("businessReports");
    if (res?.status && Array.isArray(res.data)) {
      setRows(
        res.data.map((row) => ({
          id: row.id || "",
          date: row.date
            ? new Date(row.date).toLocaleDateString("en-GB")
            : "",
          clientName: safe(row.client),
          clientStatus: safe(row.new_revive),
          vertical: safe(row.vertical),
          spocName: safe(row.hr_name),
          spocPhone: safe(row.hr_mobile_number),
          spocEmail: safe(row.hr_email),
          callingStatus: safe(row.calling_status),
          callingFrequency: safe(row.calling_frequency),
          businessProposal: safe(row.business_proposal),
          remarks: safe(row.remarks),
          currentCompany: safe(row.current_company),
          previousCompany: safe(row.previous_company),
        }))
      );
    } else {
      setRows([]);
    }
  };

  /* UNIQUE VALUES */
  const unique = (key) =>
    [...new Set(rows.map((x) => x[key]?.trim()).filter(Boolean))];

  /* FILTER */
  const filteredData = rows.filter((row) =>
    Object.entries(filters).every(
      ([k, v]) =>
        v === "" ||
        row[k]?.toString().toLowerCase().includes(v.toLowerCase())
    )
  );

  /* SELECTION */
  const toggleRow = (id) =>
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    selectedRows.length === filteredData.length
      ? setSelectedRows([])
      : setSelectedRows(filteredData.map((r) => r.id));

  const exportData =
    selectedRows.length > 0
      ? filteredData.filter((r) => selectedRows.includes(r.id))
      : filteredData;

  /* EXPORTS */
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BusinessReport");
    XLSX.writeFile(wb, "BusinessReport.xlsx");
  };

  const exportCSV = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "BusinessReport.csv";
    link.click();
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Business Report", 20, 10);
    doc.autoTable({
      head: [columns],
      body: exportData.map((r) => columns.map((c) => r[c])),
      styles: { fontSize: 8 },
      margin: { top: 15 },
    });
    doc.save("BusinessReport.pdf");
  };

  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

      {/* TITLE */}
      <h2 className="text-lg md:text-2xl font-bold text-center bg-gradient-to-r from-indigo-600 to-blue-400 bg-clip-text text-transparent mb-4">
        Business Report
      </h2>

      {/* COMPACT FILTER BAR */}
      <div className="glass-card p-3 mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:items-center">
        {/* SEARCH */}
        <div className="relative col-span-2 sm:w-64">
          <FaSearch className="absolute top-2 left-2 text-gray-500 text-xs" />
          <input
            className="glass-input pl-8 pr-3 py-1.5 rounded-xl outline-none w-full text-xs"
            placeholder="Search Client"
            onChange={(e) =>
              setFilters({ ...filters, clientName: e.target.value })
            }
          />
        </div>

        {/* CLIENT STATUS */}
        <select
          className="glass-input px-3 py-1.5 text-xs rounded-xl outline-none w-full sm:w-auto"
          onChange={(e) =>
            setFilters({ ...filters, clientStatus: e.target.value })
          }
        >
          <option value="">Client Status</option>
          {unique("clientStatus").map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>

        {/* VERTICAL */}
        <select
          className="glass-input px-3 py-1.5 text-xs rounded-xl outline-none w-full sm:w-auto"
          onChange={(e) =>
            setFilters({ ...filters, vertical: e.target.value })
          }
        >
          <option value="">Vertical</option>
          {unique("vertical").map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>

        {/* CALLING STATUS */}
        <select
          className="glass-input px-3 py-1.5 text-xs rounded-xl outline-none w-full sm:w-auto"
          onChange={(e) =>
            setFilters({ ...filters, callingStatus: e.target.value })
          }
        >
          <option value="">Calling Status</option>
          {unique("callingStatus").map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>

        {/* EXPORT */}
        <div className="flex gap-3 col-span-2 sm:ml-auto justify-end">
          <button onClick={exportExcel} className="text-green-600">
            <FaFileExcel size={16} />
          </button>
          <button onClick={exportCSV} className="text-yellow-500">
            <FaFileCsv size={16} />
          </button>
          <button onClick={exportPDF} className="text-red-500">
            <FaFilePdf size={16} />
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="glass-card overflow-x-auto max-h-[65vh]">
        <table className="min-w-[1100px] w-full text-sm whitespace-nowrap">
          <thead className="bg-slate-50/20 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-semibold text-[11px] uppercase tracking-wider sticky top-0">
            <tr>
              <th className="p-2 text-center">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredData.length &&
                    filteredData.length > 0
                  }
                  onChange={toggleAll}
                />
              </th>
              {columns.map((c) => (
                <th
                  key={c}
                  className="p-2 text-left uppercase text-[11px]"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>

                {columns.map((c) => (
                  <td key={c} className="p-2">
                    {row[c]}
                  </td>
                ))}
              </tr>
            ))}

            {filteredData.length === 0 && (
              <tr>
                <td colSpan="20" className="p-6 text-center text-gray-500">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
